import React, { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useGoal } from "@/context/GoalContext";
import { Target, Sparkles, RefreshCw, Palette, BellOff, Bell, ArrowRight, Check, Wand2, Share2, ChevronDown, ChevronUp } from "lucide-react";
import ShareVisionBoard from "@/components/app/ShareVisionBoard";
import GoalBuildingOverlay from "@/components/app/GoalBuildingOverlay";

const EXAMPLES = [
  "Buy a home",
  "Start a restaurant",
  "Launch my LLC",
  "$50k business LOC",
  "Buy a Tesla",
  "Wedding in 2027",
  "Retire at 55",
  "MBA at NYU",
  "Family medical fund",
];

export default function GoalCockpit() {
  const { goal, saving, setGoalText, regenerateImage, setTheme, togglePopups } = useGoal();
  const [text, setText] = useState("");
  const [showThemes, setShowThemes] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [buildingGoalText, setBuildingGoalText] = useState("");
  const [readyPulse, setReadyPulse] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  // Auto-open plan once when a brand-new goal is saved; persist the "seen"
  // state so returning users don't get a spammy repeat pulse.
  const planSeenKey = goal?.goal_label ? `cq_plan_seen_${goal.goal_label}` : "";
  const [planSeen, setPlanSeen] = useState(true);
  React.useEffect(() => {
    if (!planSeenKey) return;
    setPlanSeen(localStorage.getItem(planSeenKey) === "1");
  }, [planSeenKey]);
  React.useEffect(() => {
    if (planOpen && planSeenKey && !planSeen) {
      try { localStorage.setItem(planSeenKey, "1"); } catch {}
      setPlanSeen(true);
    }
  }, [planOpen, planSeenKey, planSeen]);

  const active = goal?.has_goal;
  const theme = goal?.theme;
  const themes = goal?.themes || [];

  const submit = async () => {
    const t = text.trim();
    if (t.length < 3) {
      toast.error("Give us at least a few words — the more you type, the smarter the plan.");
      return;
    }
    try {
      setBuildingGoalText(t);
      setReadyPulse(false);
      await setGoalText(t);
      setReadyPulse(true);
      // Small delay so the confetti burst gets seen before the card swaps in
      setTimeout(() => { setBuildingGoalText(""); toast.success("Your plan is ready ✨"); }, 1400);
      setText("");
    } catch (e) {
      setBuildingGoalText("");
      toast.error("Couldn't save that — try once more.");
    }
  };

  const detailScore = useMemo(() => {
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    if (words < 3) return { level: "Too vague", pct: 15, tone: "text-slate-400" };
    if (words < 8) return { level: "Getting warmer", pct: 45, tone: "text-amber-500" };
    if (words < 15) return { level: "Great detail", pct: 75, tone: "text-emerald-600" };
    return { level: "Chef's kiss ✨", pct: 100, tone: "text-emerald-600" };
  }, [text]);

  // ------- Idle state: goal not set -------
  if (!active) {
    return (
      <>
      <GoalBuildingOverlay open={!!buildingGoalText} onDone={readyPulse ? "READY" : ""} goalText={buildingGoalText} />
      <Card
        data-testid="goal-cockpit-idle"
        className="relative overflow-hidden rounded-3xl border-2 border-amber-300/60 bg-gradient-to-br from-white via-amber-50 to-yellow-50 shadow-xl shadow-amber-500/10"
      >
        {/* Shimmer */}
        <div className="absolute inset-0 pointer-events-none opacity-40" style={{
          background: "linear-gradient(120deg, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%)",
          backgroundSize: "200% 100%",
          animation: "cq-goal-shimmer 5s linear infinite",
        }} />
        <div className="relative p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-amber-300 px-3 py-1 text-[10px] font-black uppercase tracking-[0.28em]">
              <Wand2 size={11} /> New · Personal AI
            </span>
            <span className="text-[10px] uppercase tracking-widest font-black text-slate-500">Change your theme · custom to you</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
            What's the win you're playing for?
          </h2>
          <p className="text-slate-600 mt-2 max-w-xl">
            Tell us your goal — buying a home, opening a restaurant, launching a business.
            We'll retune the whole portal around it: <b>custom theme, AI-generated hero art, tailored recommendations,</b> and
            smart tips that show up as you explore.
          </p>

          {/* Example chips */}
          <div className="mt-4 flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                onClick={() => setText(ex)}
                className="rounded-full border border-amber-300 bg-white hover:bg-amber-100 text-slate-700 hover:text-slate-900 text-xs font-bold px-3 py-1.5 transition"
                data-testid={`goal-example-${ex.toLowerCase().replace(/\s+/g,'-')}`}
              >
                {ex}
              </button>
            ))}
          </div>

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Buy a 3-family duplex in Atlanta by end of 2027 so I can house-hack rentals…"
            className="mt-4 min-h-[100px] bg-white border-slate-200 focus-visible:ring-amber-400 text-sm"
            data-testid="goal-cockpit-input"
          />

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
                <span className={detailScore.tone}>{detailScore.level}</span>
                <span className="text-slate-400">More detail = smarter plan</span>
              </div>
              <div className="h-1.5 mt-1 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all" style={{ width: `${detailScore.pct}%` }} />
              </div>
            </div>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg"
              data-testid="goal-cockpit-submit"
            >
              {saving ? "Cooking your plan…" : <>Build my journey <ArrowRight size={13} className="ml-1" /></>}
            </Button>
          </div>
        </div>
        <style>{`@keyframes cq-goal-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
      </Card>
      </>
    );
  }

  // ------- Active state: goal set, show plan -------
  return (
    <>
    <GoalBuildingOverlay open={!!buildingGoalText} onDone={readyPulse ? "READY" : ""} goalText={buildingGoalText} />
    <Card
      data-testid="goal-cockpit-active"
      className="relative overflow-hidden rounded-3xl border-2 shadow-xl"
      style={{
        borderColor: theme?.primary ? `${theme.primary}55` : "#F59E0B55",
        background: theme?.bg_gradient || "linear-gradient(135deg,#fffbeb,#fef3c7)",
      }}
    >
      {/* Hero image */}
      {goal.hero_image_data_url && (
        <div className="relative h-56 sm:h-64 w-full overflow-hidden">
          <img
            src={goal.hero_image_data_url}
            alt={goal.goal_label}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="goal-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 text-white">
            <p className="text-[10px] uppercase tracking-[0.28em] font-black text-amber-300 flex items-center gap-1">
              <Target size={11} /> Your goal · {theme?.label}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-black leading-tight mt-1 drop-shadow">
              <span className="mr-2">{theme?.emoji}</span>{goal.goal_label}
            </h2>
            <p className="text-white/85 text-sm mt-1 italic max-w-xl">"{goal.goal_text}"</p>
          </div>
        </div>
      )}

      <div className="p-4 sm:p-5">
        {/* Compact accordion header — tap anywhere to expand the plan */}
        <button
          type="button"
          onClick={() => setPlanOpen((v) => !v)}
          className={`relative w-full flex items-center gap-3 text-left group rounded-2xl p-2 -m-2 transition
            ${!planSeen ? "cq-plan-breathe" : "hover:bg-slate-50"}`}
          aria-expanded={planOpen}
          data-testid="goal-plan-toggle"
          style={!planSeen ? { "--cq-plan-primary": theme?.primary || "#F59E0B" } : undefined}
        >
          <div
            className="relative h-11 w-11 shrink-0 rounded-2xl flex items-center justify-center text-white shadow-md transition group-hover:scale-105"
            style={{ background: theme?.primary || "#F59E0B" }}
          >
            <Target size={18} />
            {!planSeen && (
              <span
                aria-hidden
                className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-white ring-2 cq-plan-dot"
                style={{ boxShadow: `0 0 0 2px ${theme?.primary || "#F59E0B"}`, "--cq-plan-primary": theme?.primary || "#F59E0B" }}
              />
            )}
          </div>
          <div className="relative flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.28em] font-black truncate flex items-center gap-1.5" style={{ color: theme?.primary || "#F59E0B" }}>
              {theme?.tagline || "Your personal plan"}
              {!planSeen && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest"
                  style={{ background: `${theme?.primary || "#F59E0B"}18`, color: theme?.primary || "#F59E0B" }}
                  data-testid="goal-plan-new-badge"
                >
                  New
                </span>
              )}
            </p>
            <p className="text-sm font-bold text-slate-900 leading-tight mt-0.5">
              {(goal.recommendations || []).length} personalized moves for you
              <span className="text-slate-400 font-normal"> · {planOpen ? "tap to collapse" : "tap to view"}</span>
            </p>
          </div>
          <span
            className={`relative shrink-0 h-9 w-9 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-600 group-hover:border-slate-400 transition
              ${!planSeen ? "cq-plan-chevron-nudge" : ""}`}
          >
            {planOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </button>

        {/* Share vision — always visible, sits below the accordion header */}
        <div className="mt-3 flex justify-end">
          <Button
            size="sm"
            onClick={(e) => { e.stopPropagation(); setShowShare(true); }}
            className="bg-slate-900 text-amber-200 hover:text-white hover:bg-slate-800"
            data-testid="goal-share-open"
          >
            <Share2 size={13} className="mr-1" /> Share vision
          </Button>
        </div>

        {/* Expandable body */}
        {planOpen && (
        <div className="mt-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
        {/* Controls row */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setShowThemes((v) => !v); }}
            className="border-slate-200"
            data-testid="goal-theme-picker-toggle"
          >
            <Palette size={13} className="mr-1" /> Change theme
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              toast.info("Regenerating your hero image with AI…");
              try { await regenerateImage(); toast.success("New hero image ready ✨"); }
              catch { toast.error("Try again in a moment."); }
            }}
            disabled={saving}
            className="border-slate-200"
            data-testid="goal-regen-image"
          >
            <RefreshCw size={13} className={`mr-1 ${saving ? "animate-spin" : ""}`} /> Regenerate art
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const paused = await togglePopups();
              toast.success(paused ? "Coach tips paused." : "Coach tips on — Coach Ducky is watching.");
            }}
            className="border-slate-200"
            data-testid="goal-toggle-popups"
          >
            {goal.popups_paused ? <><Bell size={13} className="mr-1" /> Resume coach</> : <><BellOff size={13} className="mr-1" /> Pause coach</>}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setText(goal.goal_text || "")}
            className="text-slate-500 hover:text-slate-800"
            data-testid="goal-edit"
          >
            <Sparkles size={13} className="mr-1" /> Refine goal
          </Button>
        </div>

        {/* Theme picker */}
        {showThemes && (
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="goal-theme-grid">
            {themes.map((t) => (
              <button
                key={t.id}
                onClick={async () => { await setTheme(t.id); setShowThemes(false); toast.success(`Theme: ${t.label}`); }}
                className={`rounded-xl border-2 p-2 text-left transition ${
                  theme?.id === t.id ? "border-slate-900 shadow-md" : "border-slate-200 hover:border-slate-400"
                }`}
                style={{ background: t.bg_gradient }}
                data-testid={`goal-theme-${t.id}`}
              >
                <p className="text-lg leading-none">{t.emoji}</p>
                <p className="text-[10px] font-black uppercase tracking-wider mt-1 text-slate-800">{t.label}</p>
                {theme?.id === t.id && <p className="text-[9px] font-black text-emerald-700 mt-0.5 flex items-center gap-0.5"><Check size={9} /> Active</p>}
              </button>
            ))}
          </div>
        )}

        {/* Refine input */}
        {text && (
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-2">Refine your goal</p>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="min-h-[80px] text-sm border-slate-200"
              data-testid="goal-refine-input"
            />
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setText("")}>Cancel</Button>
              <Button size="sm" onClick={submit} disabled={saving} className="bg-slate-900 text-white" data-testid="goal-refine-submit">
                {saving ? "Rebuilding…" : "Save & rebuild plan"}
              </Button>
            </div>
          </div>
        )}

        {/* Recommendations */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] font-black mb-3" style={{ color: theme?.primary || "#F59E0B" }}>
            Your personal plan
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(goal.recommendations || []).map((r, i) => (
              <div key={i} className="rounded-xl bg-white border border-slate-100 p-3 hover:shadow-md transition" data-testid={`goal-rec-${i}`}>
                <div className="flex items-start gap-2">
                  <span
                    className="h-6 w-6 shrink-0 rounded-lg text-white flex items-center justify-center text-[11px] font-black shadow"
                    style={{ background: theme?.primary || "#F59E0B" }}
                  >
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-slate-900 leading-tight">{r.title}</p>
                    <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">{r.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>
        )}
      </div>
      {showShare && <ShareVisionBoard goal={goal} onClose={() => setShowShare(false)} />}
    </Card>
    </>
  );
}
